'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Check, Loader2, Mail } from 'lucide-react'
import { useAuth } from '@/lib/auth/context'
import { stashPendingCalc, type PendingCalc } from '@/lib/pending-calc'
import { trackLeadCapturado } from '@/lib/analytics'
import { cn } from '@/lib/utils'

interface LeadCaptureProps {
  /** Exibe só quando há resultado válido. */
  show?: boolean
  /** Origem (slug da calculadora). */
  source: string
  /** Tipo de alerta pra segmentação. */
  alertType?: string
  className?: string
  context?: Record<string, unknown>
  /** Se presente, guarda o cálculo pra salvar após criar conta. */
  calcData?: PendingCalc
  /** Pergunta do convite (padrão: cálculo + aviso do DAS). */
  titulo?: string
  /** Texto do botão. */
  botao?: string
  /** Letra miúda abaixo do campo. */
  nota?: string
}

/**
 * Captura de e-mail no resultado — SLIM e depois do download, nunca atravancando.
 *
 * Motivo de existir: tráfego de campanha (Instagram/ads) não volta sozinho. O
 * e-mail é a única segunda chance com esse visitante — vira lead recuperável
 * (aviso do DAS + o próprio cálculo). Só pra anônimo; logado já tem conta.
 */
export function LeadCapture({
  show = true,
  source,
  alertType = 'das',
  className,
  context,
  calcData,
  titulo = 'Recebe esse cálculo e o aviso do DAS no e-mail?',
  botao = 'Quero receber',
  nota = 'Sem spam · no máximo 1 e-mail por mês · cancela quando quiser.',
}: LeadCaptureProps) {
  const { user, isLoading } = useAuth()
  const [email, setEmail] = useState('')
  const [state, setState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  if (!show || isLoading) return null
  if (user) return null

  function stashForSignup() {
    if (calcData) stashPendingCalc(calcData)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!email.trim() || state === 'loading') return
    setState('loading')
    try {
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source, alertType, context }),
      })
      if (!r.ok) throw new Error('falhou')
      trackLeadCapturado(source, alertType)
      setState('done')
    } catch {
      setState('error')
    }
  }

  if (state === 'done') {
    return (
      <div className={cn('mt-4 rounded border border-brand bg-brand-faint p-3.5', className)}>
        <p className="flex items-center gap-2 text-[13.5px] font-semibold text-ink">
          <Check className="h-4 w-4 shrink-0 text-brand" /> Pronto! Você vai receber por e-mail.
        </p>
        <p className="mt-1 text-[12px] text-ink-mute">
          Quer acompanhar o MEI mês a mês?{' '}
          <Link href="/sign-up" onClick={stashForSignup} className="font-semibold text-brand hover:underline">
            Crie conta grátis →
          </Link>
        </p>
      </div>
    )
  }

  return (
    <div className={cn('mt-4 rounded border border-line bg-surface p-3.5', className)}>
      <form onSubmit={submit}>
        <p className="flex items-center gap-2 text-[13px] font-semibold text-ink">
          <Mail className="h-4 w-4 shrink-0 text-brand" />
          {titulo}
        </p>
        <div className="mt-2.5 flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu@email.com"
            className="h-10 flex-1 rounded border border-line bg-bg-alt px-3 text-[13.5px] text-ink outline-none focus:border-brand"
          />
          <button
            type="submit"
            disabled={state === 'loading'}
            className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded bg-brand px-4 text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {state === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {botao} <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </button>
        </div>
        {state === 'error' && (
          <p className="mt-2 text-[12px] text-red-600">Não rolou agora. Tenta de novo.</p>
        )}
        <p className="mt-2 text-[11px] text-ink-faint">
          {nota}
        </p>
      </form>
    </div>
  )
}
